import { useState, useEffect, useRef } from "react";

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) obs.observe(el);
    return () => { if (el) obs.unobserve(el); };
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {}, className = "" }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0px)" : "translateY(15px)",
        transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export default function Founder() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .founder-page {
          background-color: #ffffff;
          color: #1a1a1a;
          font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
          line-height: 1.6;
          padding-bottom: 80px;
        }

        .container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .top-bar {
          height: 5px;
          background: #006600;
          width: 100%;
          margin-bottom: 60px;
        }

        .founder-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: start;
        }

        .founder-text {
          font-size: 16px;
          color: #333;
        }

        .founder-title {
          font-size: 40px;
          font-weight: 800;
          color: #111;
          margin-bottom: 8px;
          letter-spacing: -1px;
        }

        .founder-subtitle {
          font-size: 18px;
          font-weight: 700;
          color: #006600;
          margin-bottom: 30px;
        }

        .founder-bio p {
          margin-bottom: 20px;
          line-height: 1.8;
          text-align: justify;
        }

        .quote-box {
          background: #f9f9f9;
          border-left: 4px solid #006600;
          padding: 24px;
          margin: 40px 0;
          font-style: italic;
          font-weight: 600;
          color: #444;
          font-size: 18px;
          line-height: 1.6;
        }

        .founder-image-area {
          position: sticky;
          top: 120px;
        }

        .founder-name-hero {
          font-size: clamp(32px, 5vw, 64px);
          font-weight: 900;
          color: #f1c40f; /* High contrast yellow to match screenshot */
          margin-bottom: 20px;
          text-align: right;
          line-height: 1.1;
        }

        .founder-img-wrapper {
          width: 100%;
          aspect-ratio: 4/5;
          background: #eee;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .founder-img-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .experience-list {
          list-style: none;
          padding: 0;
          margin-top: 30px;
        }

        .experience-item {
          margin-bottom: 15px;
          display: flex;
          gap: 12px;
        }

        .dot {
          color: #006600;
          font-weight: 900;
          flex-shrink: 0;
        }

        .sign-off {
          margin-top: 40px;
          font-weight: 800;
          color: #111;
          text-align: center;
        }

        @media (max-width: 900px) {
          .founder-grid { grid-template-columns: 1fr; gap: 40px; }
          .founder-image-area { position: static; order: -1; }
          .founder-name-hero { text-align: left; }
        }
      `}</style>

      <div className="founder-page">
        <div className="top-bar" />

        <div className="container">
          <div className="founder-grid">
            <div className="founder-text">
              <Reveal>
                <h1 className="founder-title">Our Founder</h1>
                <p className="founder-subtitle">The driving force behind SUN DISTRIBUTORS Manufacturing Company.</p>
              </Reveal>

              <div className="founder-bio">
                <Reveal delay={0.1}>
                  <p>
                    <strong>Mr. Mylapalli Tatayya</strong> — a visionary entrepreneur, marketer, and mentor — is the Founder and Chairman of <strong>SUN DISTRIBUTORS Manufacturing Company</strong>.
                  </p>
                </Reveal>

                <Reveal delay={0.15}>
                  <p>
                    With over two decades of experience in marketing, business development, network building, and leadership, he has dedicated his life to empowering individuals to become successful entrepreneurs.
                  </p>
                </Reveal>

                <Reveal delay={0.2}>
                  <p>
                    Since 2002, Mr. Tatayya has worked in multiple business sectors —
                  </p>
                  <ul className="experience-list">
                    <li className="experience-item">
                      <span className="dot">•</span>
                      <span>As a <strong>CDA in Reliance Life</strong>, gaining deep corporate and marketing knowledge.</span>
                    </li>
                    <li className="experience-item">
                      <span className="dot">•</span>
                      <span>As an <strong>Independent Distributor in RMP Network Marketing</strong>, understanding the power of team growth and people development.</span>
                    </li>
                    <li className="experience-item">
                      <span className="dot">•</span>
                      <span>As a <strong>Real Estate and House Construction Entrepreneur</strong>, managing projects with integrity and precision.</span>
                    </li>
                    <li className="experience-item">
                      <span className="dot">•</span>
                      <span>And as a <strong>Pharmacy Owner</strong>, successfully running a medical shop for 5 years and serving the healthcare needs of the community.</span>
                    </li>
                  </ul>
                </Reveal>

                <Reveal delay={0.25}>
                  <p>
                    These diverse experiences have shaped his vision — to create entrepreneurs from every corner of society, train them with real business knowledge, and guide them toward financial independence.
                  </p>
                </Reveal>

                <Reveal delay={0.3}>
                  <p>
                    Under his leadership, SUN DISTRIBUTORS stands not only as a company but as a movement for empowerment — helping youth, women, RMP doctors, and self-employed individuals to grow with knowledge, confidence, and opportunity.
                  </p>
                </Reveal>

                <Reveal delay={0.35}>
                  <div className="quote-box">
                    "Knowledge is the foundation of true success. With the right training and understanding of products, anyone can transform into a confident and capable entrepreneur."
                  </div>
                </Reveal>

                <Reveal delay={0.4}>
                  <div className="sign-off">
                    ---- Mylapalli Tatayya ----
                  </div>
                </Reveal>
              </div>
            </div>

            <div className="founder-image-area">
              <Reveal delay={0.2} style={{ height: '100%' }}>
                <div className="founder-name-hero">Mylapalli Tatayya</div>
                <div className="founder-img-wrapper" style={{ background: 'linear-gradient(135deg, #f0faf0, #e8f5e8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#006600', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '36px', fontWeight: 900, color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>M</span>
                    </div>
                    <div style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '20px', fontWeight: 800, color: '#111', letterSpacing: '0.5px' }}>Mylapalli Tatayya</div>
                    <div style={{ fontSize: '14px', color: '#006600', marginTop: '8px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>Founder &amp; Chairman</div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px', fontWeight: 500 }}>SUN DISTRIBUTORS</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
